import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, DatePicker, Button, Space, Row, Col } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, FileTextOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../services/api';
import { AuditLogFilters } from '../types';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface AuditLogFiltersProps {
  onFiltersChange: (filters: AuditLogFilters) => void;
  onExport: (format: 'csv' | 'json') => void;
  loading?: boolean;
}

const AuditLogFiltersComponent: React.FC<AuditLogFiltersProps> = ({
  onFiltersChange,
  onExport,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<Array<{ id: number; username: string; full_name?: string }>>([]);
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    fetchUsers();
    fetchActionTypes();
    fetchEntityTypes();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/users?all=1');
      const usersData = Array.isArray(response) ? response : (response as any).data || response;
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const translateActionType = (actionType: string) => {
    const actionMap: { [key: string]: string } = {
      // Asset actions
      'ASSET_CREATE': 'Tạo tài sản',
      'ASSET_UPDATE': 'Cập nhật tài sản',
      'ASSET_DELETE': 'Xóa tài sản',
      'CREATE_ASSET': 'Tạo tài sản',
      'UPDATE_ASSET': 'Cập nhật tài sản',
      'DELETE_ASSET': 'Xóa tài sản',

      // Employee actions
      'EMPLOYEE_CREATE': 'Tạo nhân viên',
      'EMPLOYEE_UPDATE': 'Cập nhật nhân viên',
      'EMPLOYEE_DELETE': 'Xóa nhân viên',
      'CREATE_EMPLOYEE': 'Tạo nhân viên',
      'UPDATE_EMPLOYEE': 'Cập nhật nhân viên',
      'DELETE_EMPLOYEE': 'Xóa nhân viên',

      // User actions
      'USER_CREATE': 'Tạo người dùng',
      'USER_UPDATE': 'Cập nhật người dùng',
      'USER_DELETE': 'Xóa người dùng',
      'CREATE_USER': 'Tạo người dùng',
      'UPDATE_USER': 'Cập nhật người dùng',
      'DELETE_USER': 'Xóa người dùng',
      'PASSWORD_CHANGE': 'Đổi mật khẩu',

      // Company actions
      'COMPANY_CREATE': 'Tạo công ty',
      'COMPANY_UPDATE': 'Cập nhật công ty',
      'COMPANY_DELETE': 'Xóa công ty',
      'CREATE_COMPANY': 'Tạo công ty',
      'UPDATE_COMPANY': 'Cập nhật công ty',
      'DELETE_COMPANY': 'Xóa công ty',

      // Location actions
      'LOCATION_CREATE': 'Tạo vị trí',
      'LOCATION_UPDATE': 'Cập nhật vị trí',
      'LOCATION_DELETE': 'Xóa vị trí',
      'CREATE_LOCATION': 'Tạo vị trí',
      'UPDATE_LOCATION': 'Cập nhật vị trí',
      'DELETE_LOCATION': 'Xóa vị trí',

      // Supplier actions
      'SUPPLIER_CREATE': 'Tạo nhà cung cấp',
      'SUPPLIER_UPDATE': 'Cập nhật nhà cung cấp',
      'SUPPLIER_DELETE': 'Xóa nhà cung cấp',
      'CREATE_SUPPLIER': 'Tạo nhà cung cấp',
      'UPDATE_SUPPLIER': 'Cập nhật nhà cung cấp',
      'DELETE_SUPPLIER': 'Xóa nhà cung cấp',

      // Category actions
      'ASSET_CATEGORY_CREATE': 'Tạo danh mục tài sản',
      'ASSET_CATEGORY_UPDATE': 'Cập nhật danh mục tài sản',
      'ASSET_CATEGORY_DELETE': 'Xóa danh mục tài sản',
      'CREATE_ASSET_CATEGORY': 'Tạo danh mục tài sản',
      'UPDATE_ASSET_CATEGORY': 'Cập nhật danh mục tài sản',
      'DELETE_ASSET_CATEGORY': 'Xóa danh mục tài sản',

      // Auth actions
      'LOGIN': 'Đăng nhập',
      'LOGOUT': 'Đăng xuất',
      'LOGIN_FAILED': 'Đăng nhập thất bại',
      'TOKEN_REFRESH': 'Làm mới token',

      // Maintenance actions
      'MAINTENANCE_CREATE': 'Tạo bảo trì',
      'MAINTENANCE_UPDATE': 'Cập nhật bảo trì',
      'MAINTENANCE_DELETE': 'Xóa bảo trì',
      'CREATE_MAINTENANCE': 'Tạo bảo trì',
      'UPDATE_MAINTENANCE': 'Cập nhật bảo trì',
      'DELETE_MAINTENANCE': 'Xóa bảo trì',

      // Inventory audit actions
      'INVENTORY_AUDIT_CREATE': 'Tạo kiểm kê',
      'INVENTORY_AUDIT_UPDATE': 'Cập nhật kiểm kê',
      'INVENTORY_AUDIT_DELETE': 'Xóa kiểm kê',
      'CREATE_INVENTORY_AUDIT': 'Tạo kiểm kê',
      'UPDATE_INVENTORY_AUDIT': 'Cập nhật kiểm kê',
      'DELETE_INVENTORY_AUDIT': 'Xóa kiểm kê',

      // Disposal actions
      'DISPOSAL_REQUEST_CREATE': 'Tạo yêu cầu thanh lý',
      'DISPOSAL_REQUEST_UPDATE': 'Cập nhật yêu cầu thanh lý',
      'DISPOSAL_REQUEST_DELETE': 'Xóa yêu cầu thanh lý',
      'CREATE_DISPOSAL_REQUEST': 'Tạo yêu cầu thanh lý',
      'UPDATE_DISPOSAL_REQUEST': 'Cập nhật yêu cầu thanh lý',
      'DELETE_DISPOSAL_REQUEST': 'Xóa yêu cầu thanh lý',

      // Transfer actions
      'TRANSFER_REQUEST_CREATE': 'Tạo yêu cầu điều chuyển',
      'TRANSFER_REQUEST_UPDATE': 'Cập nhật yêu cầu điều chuyển',
      'TRANSFER_REQUEST_DELETE': 'Xóa yêu cầu điều chuyển',
      'CREATE_TRANSFER_REQUEST': 'Tạo yêu cầu điều chuyển',
      'UPDATE_TRANSFER_REQUEST': 'Cập nhật yêu cầu điều chuyển',
      'DELETE_TRANSFER_REQUEST': 'Xóa yêu cầu điều chuyển',
    };

    return actionMap[actionType] || actionType;
  };

  const fetchActionTypes = async () => {
    try {
      const response = await apiClient.get('/system-audit-log');
      const logs = Array.isArray(response) ? response : (response as any).data || response;
      const uniqueActions = Array.from(new Set(logs.map((log: any) => log.action_type))).filter(Boolean) as string[];
      setActionTypes(uniqueActions.sort());
    } catch (error) {
      console.error('Error fetching action types:', error);
    }
  };

  const translateEntityType = (entityType: string) => {
    const entityMap: { [key: string]: string } = {
      'Asset': 'Tài sản',
      'Employee': 'Nhân viên',
      'User': 'Người dùng',
      'Company': 'Công ty',
      'Location': 'Vị trí',
      'Supplier': 'Nhà cung cấp',
      'AssetCategory': 'Danh mục tài sản',
      'Maintenance': 'Bảo trì',
      'MaintenanceType': 'Loại bảo trì',
      'MaintenanceCampaign': 'Chiến dịch bảo trì',
      'MaintenanceCampaignItem': 'Mục chiến dịch bảo trì',
      'InventoryAudit': 'Kiểm kê',
      'InventoryAuditDetail': 'Chi tiết kiểm kê',
      'DisposalRequest': 'Yêu cầu thanh lý',
      'DisposalRequestItem': 'Mục yêu cầu thanh lý',
      'DisposalRequestDocument': 'Tài liệu yêu cầu thanh lý',
      'RepairRequest': 'Yêu cầu sửa chữa',
      'TransferRequest': 'Yêu cầu điều chuyển',
      'AssetAccessory': 'Phụ kiện tài sản',
      'AssetAttachment': 'Tệp đính kèm tài sản',
    };

    return entityMap[entityType] || entityType;
  };

  const fetchEntityTypes = async () => {
    try {
      const response = await apiClient.get('/system-audit-log');
      const logs = Array.isArray(response) ? response : (response as any).data || response;
      const uniqueEntities = Array.from(new Set(logs.map((log: any) => log.target_entity_type).filter(Boolean))) as string[];
      setEntityTypes(uniqueEntities.sort());
    } catch (error) {
      console.error('Error fetching entity types:', error);
    }
  };

  const handleSearch = () => {
    const values = form.getFieldsValue();
    const filters: AuditLogFilters = {};

    if (values.user_id) filters.user_id = values.user_id;
    if (values.action_type) filters.action_type = values.action_type;
    if (values.target_entity_type) filters.target_entity_type = values.target_entity_type;
    if (values.target_entity_id) filters.target_entity_id = values.target_entity_id;
    if (values.ip_address) filters.ip_address = values.ip_address;
    if (values.date_range && values.date_range.length === 2) {
      filters.start_date = values.date_range[0].format('YYYY-MM-DD');
      filters.end_date = values.date_range[1].format('YYYY-MM-DD');
    }

    onFiltersChange(filters);
  };

  const handleReset = () => {
    form.resetFields();
    onFiltersChange({});
  };

  const handleExport = (format: 'csv' | 'json') => {
    const values = form.getFieldsValue();
    const filters: AuditLogFilters = {};

    if (values.user_id) filters.user_id = values.user_id;
    if (values.action_type) filters.action_type = values.action_type;
    if (values.target_entity_type) filters.target_entity_type = values.target_entity_type;
    if (values.target_entity_id) filters.target_entity_id = values.target_entity_id;
    if (values.ip_address) filters.ip_address = values.ip_address;
    if (values.date_range && values.date_range.length === 2) {
      filters.start_date = values.date_range[0].format('YYYY-MM-DD');
      filters.end_date = values.date_range[1].format('YYYY-MM-DD');
    }

    onExport(format);
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Bộ lọc nhật ký hệ thống</span>
          <Button
            type="text"
            icon={isCollapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={() => setIsCollapsed(!isCollapsed)}
            size="small"
            title={isCollapsed ? "Mở rộng" : "Thu gọn"}
          />
        </div>
      }
      style={{ marginBottom: 16 }}
    >
      {!isCollapsed && (
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="user_id" label="Người dùng">
                <Select
                  placeholder="Chọn người dùng"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.full_name || user.username}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="action_type" label="Loại hành động">
                <Select
                  placeholder="Chọn hành động"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {actionTypes.map(action => (
                    <Option key={action} value={action}>
                      {translateActionType(action)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="target_entity_type" label="Loại đối tượng">
                <Select
                  placeholder="Chọn đối tượng"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {entityTypes.map(entity => (
                    <Option key={entity} value={entity}>
                      {translateEntityType(entity)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="target_entity_id" label="ID Đối tượng">
                <Input placeholder="Nhập ID đối tượng" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="date_range" label="Khoảng thời gian">
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Từ ngày', 'Đến ngày']}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="ip_address" label="Địa chỉ IP">
                <Input placeholder="Nhập địa chỉ IP" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item label=" " style={{ marginBottom: 0 }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={loading}
                    title="Tìm kiếm"
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleReset}
                    disabled={loading}
                    title="Làm mới"
                  />
                  <Button
                    icon={<FileTextOutlined />}
                    onClick={() => handleExport('csv')}
                    disabled={loading}
                    title="Xuất CSV"
                  />
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => handleExport('json')}
                    disabled={loading}
                    title="Xuất JSON"
                  />
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
      {/* Khi thu gọn không hiển thị gì cả */}
    </Card>
  );
};

export default AuditLogFiltersComponent; 